// AI_CONTEXT: {
//   "docs": [
//     "epics/202609212003_CEF_confirm-email-frontend/tickets/CEF-003-social-login-prompt"
//   ],
//   "purpose": "Social SDK singleton: Facebook/Google/Apple OAuth login (each SDK initialized once, success routed to the form on screen) and social sharing",
//   "version": "1.0.0",
//   "priority": "core",
//   "stability": "volatile"
// }
/**
 * Social - Singleton for social media authentication and sharing
 *
 * SINGLETON: Manages social media SDK initialization (Facebook, Google, Apple)
 * and provides sharing functionality.
 *
 * PUBLIC API NOTE: Public methods auto-inject caller context as first argument.
 * JSDoc @param lists show only user-provided parameters (what you actually pass).
 *
 * FEATURES:
 * - OAuth login (Facebook, Google, Apple)
 * - Social sharing (Facebook, Twitter, Pinterest)
 * - SDK initialization and management
 * - Share dialogs
 *
 * @class Social
 * @extends Class
 * @singleton
 */
define("Social", [`Class`, `module`], function (Super, Self) {
	// Local variables to track the facebook api
	const FACEBOOK_TIMEOUT_DURATION = 10000;
	// fail-safe: reject an apple/google `authorize()` deferred if the SDK never calls back
	const SOCIAL_AUTH_TIMEOUT_DURATION = 60000;
	let FBpromise; // used in the event that the API isn't initialized in time to handle a user request
	let FBvalue; // used to cache the initialized facebook api

	return Super.inherit({
		namespace: Self.id,
		singleton: true,
		requires: [`DOM`, `XHR`],
		constructor(setup) {
			this.data.socialInitSuccessStatus = {
				apple: null,
				facebook: null,
				google: null,
			};

			// pending `authorize()` deferreds, resolved/rejected by the Apple/Google SDK
			// callbacks registered in `initApple`/`initGoogle` once sign in completes.
			this.data.pendingAuthorizations = {
				apple: null,
				google: null,
			};

			this.initFacebook();
		},

		api: {
			useApp: {
				async value(handler) {
					let { App } = this;

					if (!this.isObject(App)) {
						// NOTE: worst case scenario, should avoid all blocking actions
						await this.require({ deps: [`App`] });
						App = this.App;
					}

					handler.call(this, App);
				},
			},

			/**
			 * Initialize Apple Sign In SDK.
			 *
			 * @method Social#initApple
			 * @param {Object} options - Init options
			 * @param {Object} options.callbacks - Callback handlers
			 * @param {Function} options.callbacks.success - Called on successful sign in
			 * @returns {Promise} Resolves when SDK initialized
			 */
			initApple: {
				public: true,
				async value(context, options) {
					const { callbacks = {} } = options || {};
					const { data } = this;

					// The SDK's events are shared by every login form on the page, so point them at
					// the form on screen now rather than closing over one form's callbacks.
					data.appleSignInSuccessHandler = this.isFunction(callbacks.success) ? callbacks.success : null;
					this.bindAppleSignInListeners();

					if (!data.appleAuthInitPromise) {
						data.appleAuthInitPromise = this.initializeAppleSdk();
					}

					try {
						const initialized = await data.appleAuthInitPromise;

						data.socialInitSuccessStatus.apple = true;

						return initialized;
					} catch (error) {
						// let the next login form retry the download
						data.appleAuthInitPromise = null;
						data.socialInitSuccessStatus.apple = false;
					}
				},
			},

			/**
			 * Load Apple's SDK and call auth.init once per page. A second init makes the SDK
			 * dispatch AppleIDSignInOnSuccess twice for a single popup, and an Apple authorization
			 * can only be redeemed once.
			 */
			initializeAppleSdk: {
				async value() {
					const [AppleID] = await this.require({
						deps: [`https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js`],
					});
					const { Config } = this;
					const { APPLE_CLIENT_ID: clientId } = Config;
					const { origin } = window.location;

					window.AppleID = AppleID;

					return AppleID.auth.init({
						clientId,
						scope: `name email`,
						redirectURI: `${origin}/sso/apple`,
						state: this.generateRandomString(16),
						nonce: this.generateRandomString(16),
						usePopup: true,
					});
				},
			},

			/**
			 * Apple reports a sign in through document events, so the listeners belong to the SDK
			 * rather than to any one login form. Binding them once keeps reopening the login modal
			 * from posting the same authorization to /sso/apple twice, where the second post fails
			 * because the first already created the account.
			 */
			bindAppleSignInListeners: {
				value() {
					const { data } = this;

					if (data.appleSignInListenersBound === true) {
						return;
					}

					data.appleSignInListenersBound = true;

					document.addEventListener(`AppleIDSignInOnSuccess`, async (event) => {
						const token = this.appleAuthorizationToken(event);

						// Mark the authorization before posting it, so a twin event dispatched for
						// the same popup cannot redeem it a second time.
						if (this.isString(token, 1)) {
							if (data.postedAppleAuthorizationToken === token) {
								return;
							}

							data.postedAppleAuthorizationToken = token;
						}

						try {
							const user = await this.handleAppleSignInOnSuccess(event);
							const pending = data.pendingAuthorizations.apple;

							if (pending) {
								data.pendingAuthorizations.apple = null;
								pending.resolve(user);
							}

							if (this.isFunction(data.appleSignInSuccessHandler)) {
								data.appleSignInSuccessHandler(user);
							}
						} catch (reason) {
							const pending = data.pendingAuthorizations.apple;

							if (this.isString(token, 1) && data.postedAppleAuthorizationToken === token) {
								data.postedAppleAuthorizationToken = null;
							}

							// without this the form sits on a spinner until the sign in times out
							if (pending) {
								data.pendingAuthorizations.apple = null;
								pending.reject(reason);
							}
						}
					});

					document.addEventListener(`AppleIDSignInOnFailure`, (event) => {
						const pending = data.pendingAuthorizations.apple;

						if (pending) {
							data.pendingAuthorizations.apple = null;
							pending.reject(event && event.detail);
						}
					});
				},
			},

			/**
			 * The single-use credential Apple returned, used to recognize a repeated sign in event.
			 */
			appleAuthorizationToken: {
				value(event) {
					const authorization = event && event.detail && event.detail.authorization;

					if (!this.isObject(authorization)) {
						return null;
					}

					if (this.isString(authorization.id_token, 1)) {
						return authorization.id_token;
					}

					if (this.isString(authorization.code, 1)) {
						return authorization.code;
					}

					return null;
				},
			},

			initFacebook: {
				value() {
					const { Config, Deferred } = this;
					const deferred = Deferred();
					const { promise, resolve, reject } = deferred;
					const fbRoot = document.createElement(`div`);

					// Create the root node that Facebook expects
					fbRoot.id = `fb-root`;
					document.body.appendChild(fbRoot);
					window.fbAsyncInit = () => {
						// api is initialized
						const { FB } = window;

						// clear the global scope
						delete window.FB;
						delete window.fbAsyncInit;

						this.data.socialInitSuccessStatus.facebook = true;

						// resolve
						resolve(FB);
					};

					this.defer({
						duration: FACEBOOK_TIMEOUT_DURATION,
						// fail-safe timeout
						then: () => {
							this.data.socialInitSuccessStatus.facebook = false;

							reject({ message: `Facebook timeout` });
						},
					});

					// save a promise for the initialized FB API
					FBpromise = this.when({
						promise,
						then: (FB) => {
							// sdk ready (see window.fbAsyncInit above)
							const { FACEBOOK_APP_ID: appId } = Config;
							const cookie = true;
							const xfbml = true;
							// last updated Mar 2026
							const version = `v25.0`;

							FB.init({ appId, cookie, xfbml, version });
							FBvalue = FB; // cache a local reference to the api
						},
					});

					// NOTE: Don't thread into the Promise chain, avoid App circular dependency & FB SDK timeouts
					this.require({ deps: [`App`, `facebook`] });
				},
			},

			/**
			 * Initialize Google Sign In SDK.
			 *
			 * @method Social#initGoogle
			 * @param {Object} options - Init options
			 * @param {Object} options.callbacks - Callback handlers
			 * @param {Function} options.callbacks.success - Called on successful sign in
			 * @returns {Promise} Resolves when SDK initialized
			 */
			initGoogle: {
				public: true,
				async value(context, options) {
					const { Config } = this;
					let { GOOGLE_CLIENT_ID: client_id } = Config;

					try {
						await this.require({ deps: [`https://accounts.google.com/gsi/client`] });

						const { google } = window;
						const { accounts } = google;
						const { id } = accounts;
						const { callbacks } = options;

						id.initialize({
							client_id,
							callback: async (event) => {
								const user = await this.handleGoogleCredentialResponse(event);
								const pending = this.data.pendingAuthorizations.google;

								if (pending) {
									this.data.pendingAuthorizations.google = null;
									pending.resolve(user);
								}

								if (this.isFunction(callbacks.success)) {
									callbacks.success(user);
								}
							},
						});

						id.renderButton(this.DOM.get(`#google-signin`), {
							type: `icon`,
							width: `200`,
						});

						this.data.socialInitSuccessStatus.google = true;
					} catch (error) {
						this.data.socialInitSuccessStatus.google = false;
					}
				},
			},

			/**
			 * @method Social#useFacebook
			 */
			useFacebook: {
				defaults: {},
				async value(handler) {
					try {
						if (!this.isObject(FBvalue)) {
							// NOTE: this is a last resort, should avoid ever being a blocking call
							await FBpromise;
						}

						// the API is ready to use
						if (this.isFunction(handler)) {
							handler.call(this, FBvalue);
						}
					} catch (reason) {
						// an error was thrown when trying to initialize

						this.useApp((App) => {
							App.notifyUser({
								// notify the user of the error
								type: `error`,
								message: `There was an error initializing the Facebook API`,
							});
						});

						// re-throw the error immediately
						throw reason;
					}
				},
			},

			/**
			 * Authorize user with social provider.
			 *
			 * @method Social#authorize
			 * @param {Object} [options] - Authorization options
			 * @param {string} [options.provider='facebook'] - Provider: 'facebook', 'google', 'apple'
			 * @param {boolean} [options.publisher=false] - Publisher authorization flow
			 * @returns {Promise<Object>} Authorization result with accessToken
			 */
			authorize: {
				async: true,
				public: true,
				defaults: {
					provider: `facebook`,
					publisher: false,
				},
				value(context, options) {
					const { Deferred, data } = this;
					const { provider } = options;
					const deferred = Deferred();

					switch (provider) {
						case `apple`: {
							const { AppleID } = window;
							const previous = data.pendingAuthorizations.apple;

							if (previous) {
								// a prior authorize() call for this provider never settled (e.g. a
								// double-click); don't let it hang forever once superseded
								previous.reject({ message: `Superseded by a new Apple sign in attempt` });
							}

							data.pendingAuthorizations.apple = deferred;

							this.defer({
								duration: SOCIAL_AUTH_TIMEOUT_DURATION,
								then: () => {
									if (data.pendingAuthorizations.apple === deferred) {
										data.pendingAuthorizations.apple = null;
										deferred.reject({ message: `Apple sign in timeout` });
									}
								},
							});

							// `AppleIDSignInOnSuccess`/`AppleIDSignInOnFailure` (registered in
							// `initApple`) settle `data.pendingAuthorizations.apple` once the
							// popup flow completes; guard against `signIn()` itself throwing
							// (e.g. the popup was blocked) so this promise doesn't hang forever.
							try {
								AppleID.auth.signIn();
							} catch (error) {
								data.pendingAuthorizations.apple = null;
								deferred.reject(error);
							}

							break;
						}

						case `facebook`:
							{
								this.useFacebook((FB) => {
									FB.getLoginStatus((response) => {
										const facebookStatusHandler = (response) => {
											if (this.isObject(response) && this.isObject(response.authResponse)) {
												// API responded with an active session
												deferred.resolve({
													// Resolve with an authorization object, containing the access token
													accessToken: response.authResponse.accessToken,
												});
											} else {
												// Reject the original promise, there was a problem authorizing
												deferred.reject(response);
											}
										};

										// request FB login status
										if (response.status === `connected`) {
											// already logged in and app approved
											facebookStatusHandler(response);
										} else {
											this.useApp((App) => {
												const { Config } = this;
												const { PRIMARY_DOMAIN: domain } = Config;
												const { path: href } = data;
												const queryString = App.getQueryString();
												const fallback_redirect_uri = `${domain}/oauth`;
												const response_type = `token`;
												const method = `facebook`;
												const stateValue = { href, method, queryString };
												const state = JSON.stringify(stateValue);
												const loginOptions = { fallback_redirect_uri, response_type, state };

												FB.login((response) => {
													facebookStatusHandler(response);
												}, loginOptions);
											});
										}
									});
								});
							}
							break;

						case `google`: {
							const previous = data.pendingAuthorizations.google;

							if (previous) {
								// a prior authorize() call for this provider never settled (e.g. a
								// double-click); don't let it hang forever once superseded
								previous.reject({ message: `Superseded by a new Google sign in attempt` });
							}

							data.pendingAuthorizations.google = deferred;

							this.defer({
								duration: SOCIAL_AUTH_TIMEOUT_DURATION,
								then: () => {
									if (data.pendingAuthorizations.google === deferred) {
										data.pendingAuthorizations.google = null;
										deferred.reject({ message: `Google sign in timeout` });
									}
								},
							});

							// resolved/rejected by the `id.initialize` callback in `initGoogle`
							try {
								this.DOM.get(`#google-signin`).querySelector(`div[role=button]`).click();
							} catch (error) {
								data.pendingAuthorizations.google = null;
								deferred.reject(error);
							}

							break;
						}
					}

					return deferred.promise;
				},
			},

			/**
			 * Share content via social media.
			 *
			 * @method Social#share
			 * @param {Object} options - Share options
			 * @param {string} options.method - Platform: 'facebook', 'twitter', 'pinterest'
			 * @param {string} options.url - URL to share
			 * @param {string} options.message - Share message
			 * @param {string} [options.image] - Image URL
			 */
			share: {
				public: true,
				value(context, options) {
					const { image, message, method, url: passedUrl } = options;
					const encodedMessage = encodeURI(message);
					const encodedUrl = encodeURI(passedUrl);
					const encodedImage = encodeURI(image);

					switch (method) {
						case `twitter`: {
							this.useApp((App) => {
								const encodedSpace = encodeURI(` `);
								const text = `${encodedMessage}${encodedSpace}${encodedUrl}`;
								const url = `//twitter.com/intent/tweet?text=${text}&via=Tixr_`;
								const scrollbars = false;
								const toolbar = false;
								// suggested twitter dimensions
								const width = 550;
								const height = 420;

								App.popup({ height, scrollbars, toolbar, url, width });
							});
							break;
						}
						case `pinterest`: {
							this.useApp((App) => {
								const queryParams = [`url=${encodedUrl}`, `media=${encodedImage}`, `media=${encodedImage}`];
								const paramString = queryParams.join(`&`);

								App.popup({ url: `//pinterest.com/pin/create/button/?${paramString}` });
							});
							break;
						}
						case `facebook`: {
							this.useFacebook((FB) => {
								FB.ui({
									method: `feed`,
									caption: message,
									link: passedUrl,
									picture: image,
									user_message_prompt: `Tixr`,
								});
							});
							break;
						}
					}
				},
			},

			/**
			 * Fetch user's Facebook pages (for publisher accounts).
			 *
			 * @method Social#getFacebookPages
			 * @param {Object} [options] - Request options
			 * @returns {Promise<Array>} Array of Facebook page objects
			 */
			getFacebookPages: {
				async: true,
				public: true,
				value(owner, options) {
					const provider = `facebook`;
					const publisher = true;
					let promise;

					promise = this.authorize({ provider, publisher });

					promise = this.when({
						promise,
						then: () => this.useFacebook(),
					});

					promise = this.when({
						promise,
						then: (FB) => {
							const deferred = this.Deferred();
							const { promise, resolve, reject } = deferred;
							const duration = 5000;

							// timeout handling
							this.defer({
								duration,
								// NOTE: ignores if already resolved
								then: () => reject(new Error(`There was an error fetching your FB pages`)),
							});

							FB.api(`/me/accounts`, (response) => resolve(response.data));

							return promise;
						},
					});

					return this.when({
						promise,
						then: function (FB) {},
					});
				},
			},

			/**
			 * Set current page path (for OAuth redirects).
			 *
			 * @method Social#setPath
			 * @param {Object} options - Options
			 * @param {string} options.path - Current page path
			 * @returns {Promise}
			 */
			setPath: {
				async: true,
				public: true,
				value(context, { path }) {
					const { data } = this;
					data.path = path;
				},
			},

			/**
			 * Handle Apple Sign In success event.
			 *
			 * @method Social#handleAppleSignInOnSuccess
			 * @param {Event} event - Apple sign in event
			 * @returns {Promise<Object>} User object from server
			 */
			handleAppleSignInOnSuccess: {
				async: true,
				public: true,
				value(context, event) {
					const { XHR } = this;
					const { detail: data } = event;

					return XHR.ajax({
						type: `POST`,
						url: `/sso/apple`,
						REST: true,
						data,
					});
				},
			},

			/**
			 * Handle Google Sign In credential response.
			 *
			 * @method Social#handleGoogleCredentialResponse
			 * @param {Object} response - Google credential response
			 * @param {string} response.credential - JWT credential token
			 * @returns {Promise<Object>} User object from server
			 */
			handleGoogleCredentialResponse: {
				async: true,
				public: true,
				value(context, response) {
					const { XHR } = this;
					const { credential } = response;

					return XHR.ajax({
						type: `POST`,
						url: `/sso/google`,
						REST: true,
						data: { credential },
					});
				},
			},

			generateRandomString: {
				value(length) {
					const charset = `ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789`;
					let result = ``;

					for (let i = 0; i < length; i++) {
						result += charset.charAt(Math.floor(Math.random() * charset.length));
					}

					return result;
				},
			},
		},
	});
});

//# sourceMappingURL=Social.js.map